import {ForkButton} from '@github-ui/repo-fork-button'
import type {ForkabilityError} from '@github-ui/repo-fork-button/types'
import {
  NotificationsSubscriptionsMenu,
  type NotificationsSubscriptionsMenuProps,
} from '@github-ui/notifications-subscriptions-menu'
import {PinButton, shouldShowPinButton} from '@github-ui/repo-pin-button'
import type {OrgPinData, PinButtonProps} from '@github-ui/repo-pin-button/types'
import {StarButton} from '@github-ui/repo-star-button'
import {TemplateButton} from '@github-ui/repo-template-button'
import {useSearchParams} from '@github-ui/use-navigate'

import {HeaderSponsorButton} from './HeaderSponsorButton'

export interface RepoHeaderActionsProps {
  repo: {isPrivate: boolean; isTemplate: boolean}
  pin: {
    canPin: boolean
    isPinned: boolean
    isOrgOwned: boolean
    orgPinData?: OrgPinData | null
    pinItemsRemaining: number
  }
  viewer: {isLoggedIn: boolean; emuContributionBlocked: boolean}
  paths: {
    repoUrl: string
    repoNameWithOwner: string
    starPath: string
    unstarPath: string
    forkSelectPath: string
    forksApiPath: string
    templatePath: string
    listsApiPath: string
    listsUpdatePath: string
  }
  ownerLogin: string
  repoName: string
  star: {viewerHasStarred: boolean; canStar: boolean}
  stargazerCount: number
  fork: {canFork: boolean; forkabilityError?: ForkabilityError | null}
  forksCount: number
  watch: {
    canWatch: boolean
    watchData: NotificationsSubscriptionsMenuProps
  }
  showSponsorButton: boolean
  showTemplateButton: boolean
  hasAccessToCodespaces?: boolean
  onError: (message: string) => void
}

export function RepoHeaderActions({
  repo,
  pin,
  viewer,
  paths,
  ownerLogin,
  repoName,
  star,
  stargazerCount,
  fork,
  forksCount,
  watch,
  showSponsorButton,
  showTemplateButton,
  hasAccessToCodespaces,
  onError,
}: RepoHeaderActionsProps) {
  const pinProps: PinButtonProps = {
    repoNameWithOwner: paths.repoNameWithOwner,
    repoUrl: paths.repoUrl,
    isPrivate: repo.isPrivate,
    isOrgOwned: pin.isOrgOwned,
    canPin: pin.canPin,
    isPinned: pin.isPinned,
    isLoggedIn: viewer.isLoggedIn,
    pinItemsRemaining: pin.pinItemsRemaining,
    orgPinData: pin.orgPinData,
  }

  const [searchParams] = useSearchParams()
  const sponsorAutoOpen = searchParams.get('sponsor') === '1'

  return (
    <ul
      className="list-style-none d-flex flex-shrink-0 flex-wrap flex-items-center flex-justify-end gap-2 py-1"
      data-testid="repo-header-actions"
    >
      {showSponsorButton && (
        <li>
          <HeaderSponsorButton
            ownerLogin={ownerLogin}
            repoName={repoName}
            repoNameWithOwner={paths.repoNameWithOwner}
            isOrgOwned={pin.isOrgOwned}
            defaultOpen={sponsorAutoOpen}
          />
        </li>
      )}

      {shouldShowPinButton(pinProps) && (
        <li>
          <PinButton
            {...pinProps}
            onError={error => onError(error.message || 'Unable to pin repository. Please try again.')}
          />
        </li>
      )}

      {(!viewer.isLoggedIn || watch.canWatch) && (
        <li>
          <NotificationsSubscriptionsMenu {...watch.watchData} isLoggedIn={viewer.isLoggedIn} repoUrl={paths.repoUrl} />
        </li>
      )}

      <li>
        <ForkButton
          forkCount={forksCount}
          canFork={fork.canFork}
          forkabilityError={fork.forkabilityError}
          repoNameWithOwner={paths.repoNameWithOwner}
          forkSelectPath={paths.forkSelectPath}
          forksApiPath={paths.forksApiPath}
          isLoggedIn={viewer.isLoggedIn}
        />
      </li>

      <li>
        <StarButton
          starred={star.viewerHasStarred}
          starCount={stargazerCount}
          starPath={paths.starPath}
          unstarPath={paths.unstarPath}
          canStar={star.canStar}
          isLoggedIn={viewer.isLoggedIn}
          listsApiPath={paths.listsApiPath}
          listsUpdatePath={paths.listsUpdatePath}
          repoNameWithOwner={paths.repoNameWithOwner}
          emuContributionBlocked={viewer.emuContributionBlocked}
        />
      </li>

      {showTemplateButton && repo.isTemplate && (
        <li>
          <TemplateButton
            templatePath={paths.templatePath}
            showTemplateButton={showTemplateButton}
            hasCodespacesAccess={hasAccessToCodespaces ?? false}
            onError={error => onError(error.message || 'Unable to create template. Please try again.')}
          />
        </li>
      )}
    </ul>
  )
}
